/// <reference path="../../../../../typings/index.d.ts" />

module Meevo.Plugins.MeevoMessenger {

    import IMeevoMessengerSettings = Meevo.Model.IMeevoMessengerSettings;
    import IMeevoMessengerQuickAnswer = Meevo.Model.IMeevoMessengerQuickAnswer;
    import IMeevoMessengerAutoReply = Meevo.Model.IMeevoMessengerAutoReply;
    import MeevoMessengerAutoRepliesEnum = Meevo.Model.MeevoMessengerAutoRepliesEnum;
    import Employee = Meevo.Model.Employee;

    export interface IMeevoMessengerBase {
        getConversationList(conversationBodyRequest: IConversationBodyRequest, isSilent?: boolean): ng.IPromise<any>;
        removeDuplicatesFromConversationList(tempConversationPreviewList: MeevoMessengerConversation[]): void;
        processInboundMessage(message: MeevoMessengerMessage): void;
        addNewMessage(message: MeevoMessengerMessage, conversation: MeevoMessengerConversation): void;
        updateConversationInformation(id: Guid, message: MeevoMessengerMessage): void;
        reduceBadgeNumber(id: Guid): void;
        readInboundMessages(msg: MeevoMessengerMessage): void;
        getDefaultConversationBodyRequest(isClientList: boolean, additionalParams): IConversationBodyRequest;
        sendMessageCall(text: string, conversation: MeevoMessengerConversation, classification: MeevoMessengerClassificationApiType): ng.IPromise<any>;
        preparePhoneNumber(phoneNumber: string): string;
        newConversations(): MeevoMessengerConversation;
        processSettings(settings): void;
        prepareAutoReplies(settings: IMeevoMessengerSettings): void
        checkSystemTemplates(): void
        toggleAutoReply(): void
        saveMessengerSettings(): void
        openAutoReplyModal(): void;
        updateAutoReply(autoReply: IMeevoMessengerAutoReply): void
        removeSystemAutoReplyTimer(): void
        activateConversationByUser(isEmployee: boolean, selectedUser: any, phoneNumber: string | null): ng.IPromise<Guid>;
        checkedSelectedUser(selectedUser): boolean
    }

    export class MeevoMessengerBase implements IMeevoMessengerBase {

        protected identityService: Meevo.Components.Common.IIdentityService;
        protected $q: angular.IQService;
        protected $rootScope: angular.IRootScopeService;
        protected $state: any;
        protected busyService: Meevo.Components.Busy.IBusyService;
        protected meevoMessengerApiService: IMeevoMessengerApiService;
        protected meevoMessengerService: IMeevoMessengerService;
        protected $timeout: angular.ITimeoutService;
        protected $translate: angular.translate.ITranslateService;
        protected modalService: Meevo.Components.Modal.IModalService;
        protected securityChecker: Meevo.Components.Common.ISecurityService;

        protected selectedMessenger: any;

        protected notificationClassification: string | null = null;
        protected isClientMessaging: boolean | null = null;
        protected conversationPreviewList: Array<MeevoMessengerConversation> = [];
        protected conversationBodyRequest: IConversationBodyRequest = this.getDefaultConversationBodyRequest(true, {})
        protected isShowLoadMoreIcon: boolean = false;
        protected isFirstTimeLogIn: boolean = false;
        protected isPulseToggles: boolean = false;
        protected selectedConversation: MeevoMessengerConversation;
        protected countryCode: string;
        protected conversationMessageList: Array<any> = [];
        protected isNewMessage = false;
        protected newInboundMessages = [];
        protected inboundMessageTimer: any;
        protected markAsReadPromise: ng.IPromise<any>;
        protected meevoMessengerSettings: IMeevoMessengerSettings;
        protected isAccountInitialized: boolean = false;
        protected isAutoReplyEnabled: boolean = false;
        protected quickResponses: IMeevoMessengerQuickAnswer[];
        protected systemAutoReplyTimer: any;
        protected selectedAutoReply: IMeevoMessengerAutoReply;
        protected timeZoneOffset: string;
        protected autoReplyModifierUserName: string;
        protected messageTextContent: string;
        public securityKeys = Meevo.Components.Common.SecurityKeys;
        public userHasAccessToViewPhoneNumber: boolean = false;

        constructor(params) {

            this.identityService = params.identityService;
            this.$q = params.$q;
            this.$rootScope = params.$rootScope;
            this.$state = params.$state;
            this.busyService = params.busyService;
            this.meevoMessengerApiService = params.meevoMessengerApiService;
            this.meevoMessengerService = params.meevoMessengerService;
            this.$timeout = params.$timeout;
            this.$translate = params.$translate;
            this.modalService = params.modalService;
            this.securityChecker = params.securityChecker;

            this.markAsReadPromise = this.$q.resolve();

            this.userHasAccessToViewPhoneNumber = this.securityChecker.isKeyGrant(
                this.securityKeys.DATA_CLIENT_FIELDGROUP_PHONENUMBERS + Meevo.Components.Common.SecurityParts.KEY_PART_READ);

            this.initAdditionalSettings()
        }

        private initAdditionalSettings() {
            this.meevoMessengerApiService.getBusinessInfo().then(data => {
                this.timeZoneOffset = data.data['timeZoneOffset'];
            })
        }

        getConversationList(conversationBodyRequest: IConversationBodyRequest, isSilent?: boolean): ng.IPromise<any> {
            if (!isSilent) this.busyService.start();
            return this.meevoMessengerApiService.getConversationList(conversationBodyRequest)
                .then((data: Array<MeevoMessengerConversation>) => {
                    if (isSilent) {
                        const tempConversationPreviewList = [...data, ...this.conversationPreviewList];
                        this.removeDuplicatesFromConversationList(tempConversationPreviewList);
                    } else {
                        this.isShowLoadMoreIcon = data.length == 100;
                        this.conversationPreviewList = data;
                    }

                    this.isClientMessaging = conversationBodyRequest.isClientList;
                }).finally(() => {
                    this.isFirstTimeLogIn = true;
                    this.$rootScope.$broadcast(MeevoMessengerMessages.END_MODULE_PULSING, {classification: this.notificationClassification, isPulsing: false});
                    this.busyService.stop();
                });
        }

        removeDuplicatesFromConversationList(tempConversationPreviewList: MeevoMessengerConversation[]): void {
            this.conversationPreviewList = tempConversationPreviewList.reduce((accumulator: MeevoMessengerConversation[], cur: MeevoMessengerConversation) => {
                const exists = accumulator.some((elem) => elem.id === cur.id);

                if (!exists) accumulator.push(cur);

                return accumulator;
            }, ([] as Array<MeevoMessengerConversation>));
        }

        processInboundMessage(message: MeevoMessengerMessage): void {
            if (message.classification !== this.notificationClassification) return;
            if (!message.id) return;
            if (message.meevoSenderUserId === this.identityService.current().userId) return;

            const conversationId: Guid = message.conversationId;
            const isConversationExist: boolean = !!this.conversationPreviewList.find((conversation) => conversation.id === message.conversationId);
            let updatedConversationPromise: angular.IPromise<any> = this.$q.resolve();


            if (!isConversationExist) {
                const isMsgForClient: boolean = !this.isClientMessaging && !!message.clientId;
                const isMsgForEmployee: boolean = this.isClientMessaging && !!message.employeeId;

                if (!isMsgForClient && !isMsgForEmployee) {
                    if (this.selectedMessenger && this.selectedMessenger['clearConversationParams']) {
                        this.selectedMessenger.clearConversationParams();
                    }
                    updatedConversationPromise = updatedConversationPromise.then(() => this.getConversationList(this.conversationBodyRequest, true));
                }
            } else {
                const isMsgFromSelectedConversation: boolean = this.selectedConversation && this.selectedConversation.id === conversationId;
                if (!isMsgFromSelectedConversation) {
                    this.updateConversationInformation(conversationId, { ...message, isRead: false });
                }
                // If this message in not a duplicate we will add its
                else if (!this.meevoMessengerService.isMessageDuplicate(this.conversationMessageList, message.id)) {
                    if (message.hasAttachment) message = <MeevoMessengerMessage>this.meevoMessengerService.getAttachmentsForMessage(message);

                    this.addNewMessage({
                        ...message,
                        ...{
                            isRead: true,
                            senderUserName: this.meevoMessengerService.getUserName(this.selectedConversation, message)
                        }
                    });
                    this.reduceBadgeNumber(conversationId);
                    this.readInboundMessages(message);
                }
            }

            updatedConversationPromise.then(() => {
                if (this.newConversations) {
                    this.isPulseToggles = this.meevoMessengerService.isTurnOnTogglesPulsing(this.conversationPreviewList, this.newConversations[this.notificationClassification]);
                    this.$rootScope.$broadcast(MeevoMessengerMessages.END_MODULE_PULSING, {classification: this.notificationClassification, isPulsing: this.isPulseToggles});
                }
            });
        }

        addNewMessage(message: MeevoMessengerMessage, conversation: MeevoMessengerConversation = this.selectedConversation): void {
            this.countryCode = null;

            if (conversation.id) this.updateConversationInformation(conversation.id, message);
            if (!this.selectedConversation || conversation.id !== this.selectedConversation.id) return;

            this.isNewMessage = true;
            const [previousMessage, newMessage] = this.meevoMessengerService.markDifferentDays([
                ...(this.conversationMessageList.length ? this.conversationMessageList.slice(-1) : []),
                message
            ], this.timeZoneOffset, !!this.conversationMessageList.length); // Check if list is partial. The list is full if a message is first in a conversation
            this.conversationMessageList.push(newMessage || previousMessage); // If we write first message, needed message is in previousMessage variable
        }

        updateConversationInformation(id: Guid, message: MeevoMessengerMessage): void {
            const conversation: MeevoMessengerConversation = this.meevoMessengerService.findConversation(this.conversationPreviewList, { id });

            if (!conversation) return;

            conversation.lastMessage = message
        }

        reduceBadgeNumber(id: Guid): void {
            let newConversationByClassification: any;

            if(this.newConversations) {
                newConversationByClassification = this.newConversations[this.notificationClassification];
            }

            const isConversationHasNewMessages = this.newConversations && newConversationByClassification[id.toString()] && newConversationByClassification[id.toString()].length;

            if (isConversationHasNewMessages && !angular.isUndefined(isConversationHasNewMessages)) {
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.MEEVO_MESSENGER_REDUCE_BADGE_NUMBER, { conversationId: id, classification: this.notificationClassification });
                this.isPulseToggles = this.meevoMessengerService.isTurnOnTogglesPulsing(this.conversationPreviewList, this.newConversations[this.notificationClassification]);
                this.$rootScope.$broadcast(MeevoMessengerMessages.END_MODULE_PULSING, {classification: this.notificationClassification, isPulsing: this.isPulseToggles});
            }
        }

        readInboundMessages(msg: MeevoMessengerMessage): void {
            this.newInboundMessages.push({ ...msg, isRead: false });

            if (this.inboundMessageTimer) {
                this.$timeout.cancel(this.inboundMessageTimer);
                this.inboundMessageTimer = null;
            }

            this.inboundMessageTimer = this.$timeout(() => {
                this.markAsReadPromise = this.markAsReadPromise.then(() => {
                    this.meevoMessengerService.markMessagesAsRead(this.newInboundMessages, MeevoMessengerClassificationAPI.TEXTEL);
                    this.newInboundMessages = [];
                });
            }, 1000);
        }

        getDefaultConversationBodyRequest(isClientList: boolean = true, additionalParams): IConversationBodyRequest {
            const defaultParams = {
                filterPhoneNumber: null,
                filterFullName: null,
                isClientList: isClientList,
                isNewMessage: null,
                isFollowUp: null,
                debug: false,
                dateStart: moment().subtract(6, 'days').startOf('day').toDate(),
                dateEnd: moment().endOf('day').toDate(),
                isLastInbound: null,
                criteria: {
                    filter: null,
                }
            };
            return Object.assign(defaultParams, additionalParams)
        }

        sendMessageCall(text: string, conversation: MeevoMessengerConversation, classification: MeevoMessengerClassificationApiType, userId?: string): ng.IPromise<any> {
            return this.meevoMessengerApiService.sendMessage(this.preparePhoneNumber(conversation.phoneNumber), text, conversation.id, classification, this.isClientMessaging, userId).then((message: MeevoMessengerMessage) => {
                if (!this.meevoMessengerService.isMessageDuplicate(this.conversationMessageList, message.id)) {
                    this.addNewMessage({ ...message, ...{ senderUserName: this.identityService.current().displayName } }, conversation);
                }

                this.messageTextContent = '';

                return this.$timeout(() => conversation, Meevo.Plugins.MeevoMessenger.Constants.DELAY_IN_SECONDS_BETWEEN_SPLITTED_MESSAGES * 1000);
            });
        }

        preparePhoneNumber(phoneNumber: string): string {
            if (!this.securityChecker.isKeyGrant(this.securityKeys.DATA_CLIENT_FIELDGROUP_PHONENUMBERS + Meevo.Components.Common.SecurityParts.KEY_PART_READ)) {
                return null;
            }

            if (!phoneNumber) return "";

            if( phoneNumber.startsWith( "+" ) ) {
                return phoneNumber;
            }

            if (phoneNumber.startsWith("0")) {
                phoneNumber.substr(1);
            }

            if( this.countryCode && phoneNumber.startsWith( this.countryCode ) ) {
                return "+" + phoneNumber;
            }

            if( !phoneNumber.startsWith( "+" ) && !this.countryCode ) {
                return phoneNumber;
            }

            return '+' + this.countryCode + phoneNumber;
        }

        processSettings(settings): void {
            this.meevoMessengerSettings = settings;
            this.isAccountInitialized = settings.isAccountInitialized;
            this.isAutoReplyEnabled = settings.isAutoReplyEnabled;
            this.quickResponses = angular.copy(settings.quickAnswers);
            this.prepareAutoReplies(this.meevoMessengerSettings);
            this.getAutoReplyActivator(settings)

            // If manual auto-reply turn-off, check for system auto-reply
            if (!this.meevoMessengerSettings.isAutoReplyEnabled && this.meevoMessengerSettings.useBusinessHours && !this.systemAutoReplyTimer) this.checkSystemTemplates();
        }

        getAutoReplyActivator(settings): void {
            if (settings.isAutoReplyEnabled && settings.autoReplyModifier) {
                this.meevoMessengerApiService.getEmployeeById(settings.autoReplyModifier).then(employee => {
                    this.autoReplyModifierUserName = employee.userName;
                })
            }
        }

        prepareAutoReplies(settings: IMeevoMessengerSettings): void {
            this.selectedMessenger.autoReplyOptions.forEach((option: IMeevoMessengerAutoReply) => {
                switch (option.type) {
                    case MeevoMessengerAutoRepliesEnum.DefaultAutoReplyMessage:
                        option.autoReplyMessage = settings.defaultAutoReplyMessage
                        break;
                    case MeevoMessengerAutoRepliesEnum.OutsideHoursAutoReplyMessage:
                        option.autoReplyMessage = settings.outsideHoursAutoReplyMessage
                        break;
                    case MeevoMessengerAutoRepliesEnum.HolidayAutoReplyMessage:
                        option.autoReplyMessage = settings.holidayAutoReplyMessage
                        break;
                    default: break;
                }
            });

            if (this.meevoMessengerSettings.isAutoReplyEnabled)
                this.selectedAutoReply = _.find(this.selectedMessenger.autoReplyOptions, (autoReply: IMeevoMessengerAutoReply) => autoReply.type === settings.autoReplyKind);
        }

        checkSystemTemplates(): void {
            this.meevoMessengerService.getOptionsForSystemAutoReply().then((data: ISystemAutoReply) => {
                switch (data.status) {
                    case OperationModeEnum.WorkingHours:
                        console.log(`WorkingHours ${data.delay}`);
                        this.selectedAutoReply = null;
                        break;
                    case OperationModeEnum.OutsideBusinessHours:
                        console.log(`OutsideBusinessHours ${data.delay}`);
                        this.meevoMessengerApiService.getSystemAutoReply(this.notificationClassification).then((autoReplyMessage: string) => {
                            this.selectedAutoReply = {
                                type: MeevoMessengerAutoRepliesEnum.CustomAutoReply,
                                name: _.find(this.selectedMessenger.autoReplyOptions, (autoReply: IMeevoMessengerAutoReply) => {
                                    return autoReply.type === MeevoMessengerAutoRepliesEnum.OutsideHoursAutoReplyMessage;
                                }).name,
                                autoReplyMessage: autoReplyMessage
                            };
                        });

                        break;
                    default: break;
                }

                const defaultDelay: number = 1000 * 60 * 60;
                this.systemAutoReplyTimer = this.$timeout(() => {
                    this.checkSystemTemplates();
                }, data.delay >= 0 ? data.delay : defaultDelay);
            });
        }

        toggleAutoReply = (): void => {
            if (this.meevoMessengerSettings.isAutoReplyEnabled) {
                this.meevoMessengerSettings.isAutoReplyEnabled = false;
                this.isAutoReplyEnabled = false;
                this.selectedAutoReply = null;
                if (this.meevoMessengerSettings.useBusinessHours) this.checkSystemTemplates();
                this.saveMessengerSettings();
            } else {
                this.openAutoReplyModal();
            }
        }

        saveMessengerSettings(): void {
            this.busyService.start();
            // Turn on/off must save with whole settings
            this.meevoMessengerApiService.saveSettings(this.meevoMessengerSettings)
                .then((settings: IMeevoMessengerSettings) => {
                    _.assign(this.meevoMessengerSettings, settings);
                    this.getAutoReplyActivator(settings);
                    this.$rootScope.$broadcast(Meevo.Components.Common.Messages.TEXTEL_SETTINGS_UPDATE_MODEL, settings);
                })
                .finally(() => {
                    this.busyService.stop();
                });
        }

        openAutoReplyModal(): void {
            let options: Meevo.Components.Modal.IModalOptions = {
                templateUrl: "app/plugins/meevoMessenger/modals/autoReply/autoReplyModal.html",
                escapeToClose: false,
                clickOutsideToClose: false,
                locals: {
                    selectionOptions: this.selectedMessenger.autoReplyOptions,
                    handleConfirm: this.updateAutoReply,
                },
                controller: "meevo.plugins.AutoReplyModalController",
                controllerAs: "vm",
            }

            this.modalService.showCustom(options);
        }

        updateAutoReply = (autoReply: IMeevoMessengerAutoReply) => {
            this.meevoMessengerSettings.isAutoReplyEnabled = true;
            this.isAutoReplyEnabled = true;
            this.meevoMessengerSettings.autoReplyKind = autoReply.type;
            // Save information about user who turned on auto-reply for printing in auto-reply section
            this.meevoMessengerSettings.autoReplyModifier = this.identityService.current().userId;

            this.selectedAutoReply = autoReply;
            this.removeSystemAutoReplyTimer();
            this.saveMessengerSettings();
        }

        removeSystemAutoReplyTimer(): void {
            if (!this.systemAutoReplyTimer) return;

            this.$timeout.cancel(this.systemAutoReplyTimer);
            this.systemAutoReplyTimer = null;
        }

        activateConversationByUser(isEmployee: boolean, selectedUser: any, phoneNumber: string | null = null): ng.IPromise<Guid> {
            if (isEmployee && selectedUser) {
                const isUser = this.checkedSelectedUser(selectedUser);
                if (!isUser) return
            }

            let number: string | null = null;
            let countryCode: string | null = null;

            if (phoneNumber) {
                number = phoneNumber;
            } else if (selectedUser) {
                number = (selectedUser as any).primaryPhoneNumber || (selectedUser as any).phoneNumber || null;
                countryCode = (selectedUser as any).primaryPhoneNumberCountryCode || (selectedUser as any).countryCode || null;

                if (countryCode && number && this.notificationClassification == 'SMS') {
                    number = '+' + countryCode + number;
                }

            }

            if (!number) {
                return;
            }

            this.conversationBodyRequest.criteria.filter = number;
            this.conversationBodyRequest.dateEnd = null;
            this.conversationBodyRequest.dateStart = null;
            const conversationBodyRequest = _.extendOwn(
                this.conversationBodyRequest,
                this.meevoMessengerService.getFilterSettingsByFilterOption(this.conversationBodyRequest)
            );

            return this.meevoMessengerApiService.getConversationList(conversationBodyRequest).then(conversationList => {
                this.conversationBodyRequest.criteria.filter = null;

                if (conversationList.length > 0) {
                    const neededConversation = this.conversationPreviewList.find(conversation => conversation.id === conversationList[0].id)

                    if (!neededConversation) {
                        this.conversationPreviewList = [...conversationList, ...this.conversationPreviewList];
                    }

                    return conversationList[0].id;
                }
            });
        }

        /** Check employee as selected user if it has phone number **/
        checkedSelectedUser(selectedUser): boolean {
            if (this.checkedSelectedUser && !(<Employee>selectedUser).phoneNumber) {
                this.modalService.error('MESSAGE_MEEVO_MESSENGER_ERROR_NO_EMPLOYEE_PHONE_NUMBER');
                return false;
            }

            return true
        }

        displaySuggestionsModal(suggestedMessage): angular.IPromise<boolean> {
            const modalOptions = {
                okButtonContentKey: "LABEL_APPLY",
                cancelButtonContentKey: "LABEL_CANCEL",
                showCancelButton: true,
                modalClass: 'modal-alert'
            };
            return this.modalService.alert(this.$translate.instant('MESSAGE_MESSENGER_PROFANITY_SUGGESTION', { suggestedMessage }),
                Components.Modal.ModalTranslationModes.Translate, true, modalOptions, 'LABEL_REVIEW_BEFORE_SENDING');
        }

        getUserId(selectedUser) {
            if(selectedUser) {
                return selectedUser.id;
            }

            if (this.isClientMessaging) {
                return this.selectedConversation.clientId;
            }

            return this.selectedConversation.employeeId;
        }

        get newConversations() {
            return this.$state['current'].params.newConversations;
        }


    }
}